import React, { useEffect, useState, useRef } from 'react';
import { BrainIcon, ZapIcon, EyeIcon, NetworkIcon, DatabaseIcon } from 'lucide-react';
export const Disciplines = ({
  onCursorChange
}) => {
  const [activeCard, setActiveCard] = useState(null);
  const cardRefs = useRef([]);
  const sectionRef = useRef(null);
  useEffect(() => {
    // Animation on scroll
    const observer = new IntersectionObserver(entries => {
      entries.forEach((entry, index) => {
        if (entry.isIntersecting) {
          // Staggered animation delay
          setTimeout(() => {
            entry.target.classList.add('animate-in');
          }, index * 150);
        }
      });
    }, {
      threshold: 0.1
    });
    // Observe all card elements
    cardRefs.current.forEach(card => {
      if (card) observer.observe(card);
    });
    return () => {
      cardRefs.current.forEach(card => {
        if (card) observer.unobserve(card);
      });
    };
  }, []);
  // Handle mouse movement for 3D card effect
  const handleMouseMove = (e, index) => {
    if (!cardRefs.current[index]) return;
    const card = cardRefs.current[index];
    const rect = card.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    // Calculate position relative to center
    const centerX = rect.width / 2;
    const centerY = rect.height / 2;
    const rotateX = (y - centerY) / 30;
    const rotateY = (centerX - x) / 30;
    // Apply rotation
    card.style.transform = `perspective(1000px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) scale3d(1.02, 1.02, 1.02)`;
    // Update highlight position
    const highlight = card.querySelector('.card-highlight');
    if (highlight) {
      highlight.style.background = `radial-gradient(circle at ${x}px ${y}px, rgba(94, 231, 223, 0.2) 0%, rgba(94, 231, 223, 0) 50%)`;
    }
    setActiveCard(index);
  };
  // Reset card on mouse leave
  const handleMouseLeave = index => {
    if (!cardRefs.current[index]) return;
    const card = cardRefs.current[index];
    card.style.transform = 'perspective(1000px) rotateX(0) rotateY(0) scale3d(1, 1, 1)';
    const highlight = card.querySelector('.card-highlight');
    if (highlight) {
      highlight.style.background = 'none';
    }
    setActiveCard(null);
    onCursorChange('default');
  };
  const disciplines = [{
    title: 'Biological Corruption',
    description: 'Distorting natural systems into unrecognizable forms. Creating hybrid organisms that should not exist.',
    image: 'https://images.unsplash.com/photo-1500630417200-63156e226754?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=800&q=80',
    icon: <BrainIcon size={20} data-id="element-512" />,
    stats: {
      efficiency: '87%',
      stability: 'VARIABLE',
      subjects: '28'
    }
  }, {
    title: 'Synthetic Flesh',
    description: 'Forcing technology to fuse with living tissue. Exploring the violent intersection of organic matter and artificial constructs.',
    image: 'https://images.unsplash.com/photo-1532187863486-abf9dbad1b69?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=800&q=80',
    icon: <ZapIcon size={20} data-id="element-513" />,
    stats: {
      efficiency: '92%',
      stability: 'UNSTABLE',
      subjects: '47'
    }
  }, {
    title: 'Sensory Violation',
    description: 'Creating experiences that disturb the fundamental senses. Forcing viewers to confront uncomfortable biological realities.',
    image: 'https://images.unsplash.com/photo-1573164574572-cb89e39749b4?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=800&q=80',
    icon: <EyeIcon size={20} data-id="element-514" />,
    stats: {
      efficiency: '95%',
      stability: 'OPTIMAL',
      subjects: '104'
    }
  }, {
    title: 'Neural Extraction',
    description: 'Visualizing the raw data of consciousness. Making the invisible patterns of thought tangible through disturbing manifestations.',
    image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=800&q=80',
    icon: <NetworkIcon size={20} data-id="element-515" />,
    stats: {
      efficiency: '89%',
      stability: 'ENHANCED',
      subjects: '32'
    }
  }, {
    title: 'Parasitic Materials',
    description: 'Developing substances that consume and transform their environment. Materials that behave with unsettling autonomy.',
    image: 'https://images.unsplash.com/photo-1569163139599-0f4517e36f40?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=800&q=80',
    icon: <div size={20} data-id="element-516" />,
    stats: {
      efficiency: '78%',
      stability: 'VOLATILE',
      subjects: '19'
    }
  }, {
    title: 'Digital Infection',
    description: 'Creating algorithms that mimic viral behavior. Systems that corrupt and transform digital environments into disturbing new forms.',
    image: 'https://images.unsplash.com/photo-1566837945700-30057527ade0?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=800&q=80',
    icon: <DatabaseIcon size={20} data-id="element-517" />,
    stats: {
      efficiency: '96%',
      stability: 'SPREADING',
      subjects: 'UNKNOWN'
    }
  }];
  return <section id="disciplines" ref={sectionRef} className="w-full py-24 relative overflow-hidden bg-[#060a10]" data-id="element-518">
      <style jsx global data-id="element-519">{`
        @keyframes card-reveal {
          0% {
            opacity: 0;
            transform: translateY(40px);
          }
          100% {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .discipline-card {
          opacity: 0;
          transform: translateY(40px);
          transition: transform 0.3s ease-out;
          transform-style: preserve-3d;
        }
        .discipline-card.animate-in {
          animation: card-reveal 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94)
            forwards;
        }
        .card-highlight {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          pointer-events: none;
          z-index: 10;
          opacity: 0.8;
        }
        .card-content {
          transform-style: preserve-3d;
        }
        .card-3d-element {
          transform: translateZ(20px);
        }
        .card-3d-element-sm {
          transform: translateZ(10px);
        }
        @keyframes title-glitch {
          0% {
            transform: translate(0);
          }
          10% {
            transform: translate(-2px, 1px);
          }
          20% {
            transform: translate(2px, -1px);
          }
          30%,
          100% {
            transform: translate(0);
          }
        }
        .discipline-icon {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 40px;
          height: 40px;
          border-radius: 8px;
          background: rgba(94, 231, 223, 0.1);
          color: #5ee7df;
          border: 1px solid rgba(94, 231, 223, 0.2);
          transition: all 0.3s ease;
        }
        .discipline-card:hover .discipline-icon {
          background: rgba(94, 231, 223, 0.2);
          transform: translateZ(30px);
        }
        .discipline-stat {
          display: flex;
          align-items: center;
          justify-content: space-between;
          font-family: 'JetBrains Mono', monospace;
          font-size: 0.7rem;
          color: #8a9cb9;
          margin-bottom: 4px;
        }
        .discipline-stat-value {
          color: #e64cff;
        }
        .tech-grid {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-image:
            linear-gradient(
              to right,
              rgba(94, 231, 223, 0.05) 1px,
              transparent 1px
            ),
            linear-gradient(
              to bottom,
              rgba(94, 231, 223, 0.05) 1px,
              transparent 1px
            );
          background-size: 20px 20px;
          opacity: 0;
          transition: opacity 0.5s ease;
          pointer-events: none;
        }
        .discipline-card:hover .tech-grid {
          opacity: 0.2;
        }
        .interface-corner {
          position: absolute;
          width: 10px;
          height: 10px;
          border-color: rgba(94, 231, 223, 0.7);
          opacity: 0;
          transition: all 0.3s ease;
        }
        .corner-tl {
          top: 5px;
          left: 5px;
          border-top: 1px solid;
          border-left: 1px solid;
        }
        .corner-tr {
          top: 5px;
          right: 5px;
          border-top: 1px solid;
          border-right: 1px solid;
        }
        .corner-bl {
          bottom: 5px;
          left: 5px;
          border-bottom: 1px solid;
          border-left: 1px solid;
        }
        .corner-br {
          bottom: 5px;
          right: 5px;
          border-bottom: 1px solid;
          border-right: 1px solid;
        }
        .discipline-card:hover .interface-corner {
          opacity: 1;
          width: 20px;
          height: 20px;
        }
      `}</style>
      {/* Background effects */}
      <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-30 mix-blend-overlay" data-id="element-520"></div>
      <div className="absolute inset-0 bg-[url('https://uploadthingy.s3.us-west-1.amazonaws.com/cuc9dXYA9UJioCcUhxXypA/Gemini_Generated_Image_5gvci65gvci65gvc.png')] bg-center bg-no-repeat bg-cover opacity-5 mix-blend-color-burn" data-id="element-521"></div>
      <div className="container mx-auto px-6 relative z-10" data-id="element-522">
        <div className="text-center mb-16" data-id="element-523">
          <h2 className="text-3xl md:text-5xl font-bold mb-4 text-gradient font-heading animate-fade-in-up" onMouseEnter={() => onCursorChange('text')} onMouseLeave={() => onCursorChange('default')} data-id="element-524">
            METHODOLOGIES
          </h2>
          <p className="text-[#8a9cb9] max-w-2xl mx-auto font-mono animate-fade-in-up" style={{
          animationDelay: '0.2s'
        }} onMouseEnter={() => onCursorChange('text')} onMouseLeave={() => onCursorChange('default')} data-id="element-525">
            Experimental procedures for extracting unsettling truths from the
            intersection of living and artificial systems.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-526">
          {disciplines.map((discipline, index) => <div key={index} ref={el => cardRefs.current[index] = el} className="discipline-card glass overflow-hidden relative group border border-[#1a2a40]/50 hover:border-[#5ee7df]/50 transition-all duration-500 rounded-lg" onMouseMove={e => handleMouseMove(e, index)} onMouseEnter={() => onCursorChange('image')} onMouseLeave={() => handleMouseLeave(index)} style={{
          animationDelay: `${index * 100}ms`
        }} data-id="element-527">
              <div className="card-highlight" data-id="element-528"></div>
              <div className="tech-grid" data-id="element-529"></div>
              {/* Interface corners */}
              <div className="interface-corner corner-tl" data-id="element-530"></div>
              <div className="interface-corner corner-tr" data-id="element-531"></div>
              <div className="interface-corner corner-bl" data-id="element-532"></div>
              <div className="interface-corner corner-br" data-id="element-533"></div>
              <div className="card-content" data-id="element-534">
                <div className="h-48 overflow-hidden relative" data-id="element-535">
                  <img src={discipline.image} alt={discipline.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110 filter contrast-125 brightness-75 grayscale group-hover:grayscale-0 card-3d-element-sm" data-id="element-536" />
                  {/* Overlay gradient */}
                  <div className="absolute inset-0 bg-gradient-to-t from-[#000000] via-[#000000]/50 to-transparent opacity-70 card-3d-element-sm" data-id="element-537"></div>
                  {/* Scan line effect */}
                  <div className="absolute inset-0 bg-[linear-gradient(transparent_50%,rgba(16,22,36,0.2)_50%)] bg-[size:100%_4px] opacity-0 group-hover:opacity-40 transition-opacity duration-500 animate-[scanlines_8s_linear_infinite] card-3d-element" data-id="element-538"></div>
                </div>
                <div className="p-6 relative" data-id="element-539">
                  <div className="flex items-start justify-between mb-4" data-id="element-540">
                    <div className="discipline-icon card-3d-element" data-id="element-541">
                      {discipline.icon}
                    </div>
                    <div className="text-xs text-[#8a9cb9] font-mono bg-[#0a1018]/70 px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-500 card-3d-element" data-id="element-542">
                      <span className="inline-block w-2 h-2 rounded-full bg-[#5ee7df] animate-pulse mr-2" data-id="element-543"></span>
                      ACTIVE
                    </div>
                  </div>
                  <h3 className="text-xl font-bold mb-2 text-[#5ee7df] font-heading card-3d-element group-hover:animate-[title-glitch_2s_infinite]" data-id="element-544">
                    {discipline.title}
                  </h3>
                  <p className="text-[#8a9cb9] font-mono card-3d-element-sm mb-4" data-id="element-545">
                    {discipline.description}
                  </p>
                  {/* Stats */}
                  <div className="bg-[#0a1018]/50 p-3 rounded border border-[#1a2a40]/50 mt-4 opacity-0 group-hover:opacity-100 transition-opacity duration-500 card-3d-element" data-id="element-546">
                    <div className="discipline-stat" data-id="element-547">
                      <span data-id="element-548">EFFICIENCY:</span>
                      <span className="discipline-stat-value" data-id="element-549">
                        {discipline.stats.efficiency}
                      </span>
                    </div>
                    <div className="discipline-stat" data-id="element-550">
                      <span data-id="element-551">STABILITY:</span>
                      <span className="discipline-stat-value" data-id="element-552">
                        {discipline.stats.stability}
                      </span>
                    </div>
                    <div className="discipline-stat" data-id="element-553">
                      <span data-id="element-554">SUBJECTS:</span>
                      <span className="discipline-stat-value" data-id="element-555">
                        {discipline.stats.subjects}
                      </span>
                    </div>
                  </div>
                  {/* Indicator */}
                  <div className="absolute bottom-3 right-3 w-3 h-3 rounded-full bg-[#5ee7df] opacity-0 group-hover:opacity-70 transition-all duration-500 animate-pulse card-3d-element" data-id="element-556"></div>
                </div>
                {/* Corner accents */}
                <div className="absolute top-0 left-0 w-4 h-4 border-t border-l border-[#5ee7df] opacity-0 group-hover:opacity-70 transition-all duration-500 group-hover:w-8 group-hover:h-8 card-3d-element" data-id="element-557"></div>
                <div className="absolute bottom-0 right-0 w-4 h-4 border-b border-r border-[#5ee7df] opacity-0 group-hover:opacity-70 transition-all duration-500 group-hover:w-8 group-hover:h-8 card-3d-element" data-id="element-558"></div>
              </div>
              {/* 3D floating elements */}
              {activeCard === index && <>
                  {[...Array(3)].map((_, i) => <div key={i} className="absolute w-2 h-2 rounded-full border border-[#5ee7df] opacity-40 transition-all duration-500" style={{
              top: `${20 + i * 20}%`,
              left: `${10 + i * 30}%`,
              transform: `translateZ(${60 + i * 10}px)`,
              animation: `float-subtle ${2 + i * 0.5}s infinite ease-in-out ${i * 0.2}s`
            }} data-id="element-559"></div>)}
                </>}
            </div>)}
        </div>
      </div>
    </section>;
};